/**
 * PlanilhaItens.tsx — upload e preview da planilha de itens do DFD.
 *
 * Réplica do fluxo do new_dfd.html: upload → extração → tabela de preview
 * → busca progressiva dos códigos CATMAT/CATSER/SUS em blocos de 10, com
 * progresso textual e possibilidade de cancelar → BUSCA MANUAL por item
 * (clicar no código revisa; "Buscar" resolve o que a IA não encontrou).
 */

import {
  faBox,
  faCheckCircle,
  faExclamationTriangle,
  faFileExcel,
  faHospitalAlt,
  faSearch,
  faSpinner,
  faTools,
  faTrashAlt,
  faUserEdit,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useRef, useState } from 'react'
import {
  buscarCodigosLote,
  extrairPlanilha,
  type Catalogo,
  type ItemCatalogo,
  type ItemPlanilha,
} from '../../api/dfd'
import { BuscaCodigoModal } from './BuscaCodigoModal'

const TAMANHO_LOTE = 10

export interface EstadoPlanilha {
  itens: ItemPlanilha[]
  total: number
}

function rotuloCodigo(tipo: 'material' | 'servico', usarSus: boolean) {
  if (tipo === 'material') return 'Código CATMAT'
  return usarSus ? 'Código SUS' : 'Código CATSER'
}

function catalogoDe(tipo: 'material' | 'servico', usarSus: boolean): Catalogo {
  if (tipo === 'material') return 'catmat'
  return usarSus ? 'tabela_sus' : 'catser'
}

/** O código escolhido à mão vai nas MESMAS chaves que o create_dfd lê
 *  (codigo_catmat, inclusive para serviço) — as *_catser são espelho. */
function comCodigoManual(
  item: ItemPlanilha,
  escolhido: ItemCatalogo,
  servico: boolean,
): ItemPlanilha {
  // A descrição da planilha costuma ser mais completa que a do catálogo:
  // manter a mais longa evita truncar o item na geração (regra do original).
  const descricaoCatalogo =
    (item.descricao?.length ?? 0) > escolhido.descricao.length
      ? item.descricao
      : escolhido.descricao
  const atualizado: ItemPlanilha = {
    ...item,
    codigo_catmat: escolhido.codigo,
    descricao_catmat: descricaoCatalogo,
    grupo_catmat: escolhido.grupo,
    classe_catmat: escolhido.classe,
    manual_selection: true,
    pendente_busca: false,
  }
  if (servico) {
    atualizado.codigo_catser = escolhido.codigo
    atualizado.descricao_catser = descricaoCatalogo
    atualizado.grupo_catser = escolhido.grupo
    atualizado.classe_catser = escolhido.classe
  }
  return atualizado
}

export function PlanilhaItens({
  tipo,
  usarTabelaSus,
  aoAlternarSus,
  estado,
  aoMudar,
}: {
  tipo: 'material' | 'servico'
  usarTabelaSus: boolean
  aoAlternarSus?: (valor: boolean) => void
  estado: EstadoPlanilha | null
  aoMudar: (estado: EstadoPlanilha | null) => void
}) {
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState<{ mensagem: string; detalhes?: string[] } | null>(null)
  const [progresso, setProgresso] = useState<{ feitos: number; total: number } | null>(null)
  const [buscaManual, setBuscaManual] = useState<number | null>(null)
  const cancelar = useRef(false)
  const inputArquivo = useRef<HTMLInputElement>(null)

  const catalogo = catalogoDe(tipo, usarTabelaSus)
  const classesIcone = tipo === 'material' ? 'text-green-600' : 'text-purple-600'
  const classesCaixa =
    tipo === 'material'
      ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
      : 'border-purple-200 bg-purple-50 dark:border-purple-800 dark:bg-purple-900/20'
  const classesCabecalho =
    tipo === 'material'
      ? 'bg-green-100 dark:bg-green-900/40'
      : 'bg-purple-100 dark:bg-purple-900/40'

  async function enviar(arquivo: File) {
    setCarregando(true)
    setErro(null)
    setProgresso(null)
    cancelar.current = false
    try {
      const resposta = await extrairPlanilha(arquivo, tipo, usarTabelaSus)
      const itens = resposta.items ?? []
      aoMudar({ itens, total: resposta.total ?? itens.length })

      // Busca progressiva quando o backend não enriqueceu tudo de uma vez
      if (resposta.usar_processamento_lotes) {
        const enriquecidos = [...itens]
        setProgresso({ feitos: 0, total: enriquecidos.length })
        for (let i = 0; i < enriquecidos.length; i += TAMANHO_LOTE) {
          if (cancelar.current) break
          const bloco = enriquecidos.slice(i, i + TAMANHO_LOTE)
          try {
            const r = await buscarCodigosLote(bloco, usarTabelaSus)
            r.items?.forEach((item, j) => {
              enriquecidos[i + j] = { ...enriquecidos[i + j], ...item }
            })
          } catch {
            // bloco falhou: segue para os próximos, item fica sem código
          }
          setProgresso({
            feitos: Math.min(i + TAMANHO_LOTE, enriquecidos.length),
            total: enriquecidos.length,
          })
          aoMudar({ itens: [...enriquecidos], total: enriquecidos.length })
        }
        setProgresso(null)
      }
    } catch (e) {
      const apiErro = e as { message?: string; detalhes?: unknown }
      const detalhes = (apiErro.detalhes as { detalhes?: string[] })?.detalhes
      setErro({
        mensagem: apiErro.message ?? 'Não foi possível ler a planilha.',
        detalhes: Array.isArray(detalhes) ? detalhes : undefined,
      })
      aoMudar(null)
    } finally {
      setCarregando(false)
    }
  }

  function remover() {
    aoMudar(null)
    setErro(null)
    setProgresso(null)
    if (inputArquivo.current) inputArquivo.current.value = ''
  }

  const temValores = estado?.itens.some((i) => i.valor_unitario || i.valor_total)

  return (
    <div className={`rounded-lg border p-6 ${classesCaixa}`}>
      <p className="mb-3 font-semibold text-slate-700 dark:text-slate-200">
        <FontAwesomeIcon
          icon={tipo === 'material' ? faBox : faTools}
          className={`mr-2 ${classesIcone}`}
        />
        Planilha de {tipo === 'material' ? 'Materiais' : 'Serviços'}
      </p>

      {tipo === 'servico' && aoAlternarSus && (
        <label className="mb-4 flex cursor-pointer items-center gap-3 rounded-lg border-2 border-teal-300 bg-gradient-to-r from-teal-50 to-cyan-50 p-3 dark:border-teal-700 dark:from-teal-900/20 dark:to-cyan-900/20">
          <input
            type="checkbox"
            checked={usarTabelaSus}
            onChange={(e) => aoAlternarSus(e.target.checked)}
            className="h-4 w-4 accent-teal-600"
          />
          <span className="text-sm text-slate-700 dark:text-slate-200">
            <FontAwesomeIcon icon={faHospitalAlt} className="mr-2 text-teal-600" />
            <strong>Usar Tabela SUS</strong> (procedimentos de saúde em vez de CATSER)
          </span>
        </label>
      )}

      {!estado && (
        <div>
          <input
            ref={inputArquivo}
            type="file"
            accept=".xlsx,.xls,.csv"
            disabled={carregando}
            onChange={(e) => {
              const arquivo = e.target.files?.[0]
              if (arquivo) void enviar(arquivo)
            }}
            className="block w-full text-sm text-slate-600 file:mr-4 file:rounded-lg file:border-0 file:bg-white file:px-4 file:py-2 file:text-sm file:font-semibold file:text-slate-700 hover:file:bg-slate-100 dark:text-slate-300"
          />
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            <FontAwesomeIcon icon={faFileExcel} className="mr-1" />
            Colunas obrigatórias: Descrição
            {tipo === 'material' ? ', Unidade' : ''} e Quantidade
          </p>
        </div>
      )}

      {carregando && (
        <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
          <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
          Processando planilha com IA…
        </p>
      )}

      {erro && (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
          {erro.mensagem}
          {erro.detalhes && (
            <ul className="mt-2 list-inside list-disc text-xs">
              {erro.detalhes.slice(0, 5).map((d, i) => (
                <li key={i}>{d}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {estado && (
        <div className="mt-3">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
              <FontAwesomeIcon icon={faCheckCircle} className="mr-2 text-green-600" />
              {estado.total} {estado.total === 1 ? 'item encontrado' : 'itens encontrados'}
              {progresso && (
                <span className="ml-2 text-xs text-slate-500">
                  <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
                  {progresso.feitos}/{progresso.total} códigos buscados
                </span>
              )}
            </p>
            <div className="flex gap-2">
              {progresso && (
                <button
                  type="button"
                  onClick={() => {
                    cancelar.current = true
                  }}
                  className="rounded-lg bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700"
                >
                  Parar busca
                </button>
              )}
              <button
                type="button"
                onClick={remover}
                className="rounded-lg border border-slate-300 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-white dark:border-slate-600 dark:text-slate-300"
              >
                <FontAwesomeIcon icon={faTrashAlt} className="mr-1" />
                Remover planilha
              </button>
            </div>
          </div>

          <div className="max-h-96 overflow-auto rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-dark-card">
            <table className="min-w-full text-sm">
              <thead className={classesCabecalho}>
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-200">Item</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-200">Descrição</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-200">
                    {rotuloCodigo(tipo, usarTabelaSus)}
                  </th>
                  {tipo === 'material' && (
                    <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-200">Unidade</th>
                  )}
                  <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-200">Qtd.</th>
                  {temValores && (
                    <>
                      <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-200">Valor Unit.</th>
                      <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-200">Valor Total</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {estado.itens.map((item, i) => (
                  <tr key={i} className="hover:bg-slate-50 dark:hover:bg-dark-hover">
                    <td className="px-3 py-2 text-slate-500">{item.item ?? i + 1}</td>
                    <td className="px-3 py-2 text-slate-800 dark:text-slate-100">{item.descricao}</td>
                    <td className="px-3 py-2">
                      {item.codigo_catmat ? (
                        <button
                          type="button"
                          onClick={() => setBuscaManual(i)}
                          title={`${item.descricao_catmat ?? ''}\nClique para revisar ou trocar o código`}
                          className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:hover:bg-blue-900/60"
                        >
                          <FontAwesomeIcon icon={faCheckCircle} />
                          {item.codigo_catmat}
                          {item.manual_selection && (
                            <FontAwesomeIcon icon={faUserEdit} title="Escolhido manualmente" />
                          )}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setBuscaManual(i)}
                          title="Nenhum código encontrado — buscar manualmente"
                          className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800 transition-colors hover:bg-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-300 dark:hover:bg-yellow-900/60"
                        >
                          <FontAwesomeIcon icon={faSearch} />
                          Buscar
                        </button>
                      )}
                    </td>
                    {tipo === 'material' && (
                      <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{item.unidade}</td>
                    )}
                    <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{item.quantidade}</td>
                    {temValores && (
                      <>
                        <td className="px-3 py-2 text-slate-600 dark:text-slate-300">
                          {item.valor_unitario ?? '—'}
                        </td>
                        <td className="px-3 py-2 text-slate-600 dark:text-slate-300">
                          {item.valor_total ?? '—'}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            <FontAwesomeIcon icon={faSearch} className="mr-1" />
            Clique no código para revisá-lo, ou em <strong>Buscar</strong> nos
            itens sem código, para escolher no {rotuloCodigo(tipo, usarTabelaSus)
              .replace('Código ', '')}.
          </p>
        </div>
      )}

      {estado && buscaManual !== null && estado.itens[buscaManual] && (
        <BuscaCodigoModal
          catalogo={catalogo}
          descricaoItem={estado.itens[buscaManual].descricao}
          codigoAtual={estado.itens[buscaManual].codigo_catmat}
          aoFechar={() => setBuscaManual(null)}
          aoSelecionar={(escolhido) => {
            const itens = estado.itens.map((item, indice) =>
              indice === buscaManual
                ? comCodigoManual(item, escolhido, tipo === 'servico')
                : item,
            )
            aoMudar({ ...estado, itens })
            setBuscaManual(null)
          }}
        />
      )}
    </div>
  )
}
